import React, {useEffect, useState} from "react";
import {
    AccountingDashboardDto,
    AccountingDashboardRestControllerApiFactory,
    AccountingForecast,
    AccountingForecastRestControllerApiFactory,
} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {Alert, Box, Button, Stack, Typography} from "@mui/material";
import {
    AccountBalance,
    Assignment,
    AttachMoney,
    CompareArrows,
    EventBusy,
    Savings,
    TrendingDown,
    TrendingUp,
    Warning,
} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {fmtEur} from "../../../../components/utils/numbers";
import {btnSx, EmptyState, Page, PageHeader, Panel, SkeletonRows, StatGrid, StatTile} from "../../../../components/kit";
import {eur0} from "../../billing/overview/kpis";
import {months1} from "../forecast";

const fmt = fmtEur;

export const AccountingDashboardPage = () => {
    const apiManager = useApiManager();
    const navigate = useNavigate();
    const [data, setData] = useState<AccountingDashboardDto | null>(null);
    const [loading, setLoading] = useState(true);
    // La previsión (#14560) va aparte: es más lenta y no debe retrasar el resto del panel.
    const [forecast, setForecast] = useState<AccountingForecast | null | undefined>(undefined);

    useEffect(() => {
        const ctrl = apiManager.factory(AccountingDashboardRestControllerApiFactory);
        apiManager.execute(ctrl.getDashboard())
            .then(d => setData(d as AccountingDashboardDto))
            .finally(() => setLoading(false));
        apiManager.execute(apiManager.factory(AccountingForecastRestControllerApiFactory).forecast())
            .then(setForecast)
            .catch(() => setForecast(null));
    }, []);

    if (loading) return (
        <Page>
            <PageHeader title="Dashboard contable"/>
            <SkeletonRows rows={2} height={96}/>
        </Page>
    );

    if (!data?.periodId) return (
        <Page>
            <PageHeader title="Dashboard contable"/>
            <Panel>
                <EmptyState icon={<EventBusy/>} title="No hay ningún período contable abierto"
                            description="Abre uno en Períodos para ver aquí la tesorería, el resultado y los pendientes."/>
            </Panel>
        </Page>
    );

    const result = data.periodResult ?? 0;
    const supplierCount = data.pendingSupplierBillsCount ?? 0;
    const clientCount = data.pendingClientBillsCount ?? 0;

    return (
        <Page>
            <PageHeader title="Panel contable"
                        subtitle={`Período: ${data.periodName && data.periodName.includes(String(data.periodYear)) ? data.periodName : `${data.periodName || ""} ${data.periodYear || ""}`.trim()}`}/>

            <StatGrid>
                <StatTile
                    label="Tesorería (cuentas 57)"
                    value={fmt(data.cashBalance)}
                    icon={<AccountBalance fontSize="small"/>}
                    tone={data.cashBalance! >= 0 ? "success" : "error"}
                />
                <StatTile
                    label="Resultado del período"
                    value={fmt(result)}
                    hint={<Typography variant="caption" color="text.secondary">
                        Ingresos {fmt(data.periodIncome)} · Gastos {fmt(data.periodExpenses)}
                    </Typography>}
                    icon={result >= 0 ? <TrendingUp fontSize="small"/> : <TrendingDown fontSize="small"/>}
                    tone={result >= 0 ? "success" : "error"}
                />
                {forecastTiles(forecast, () => navigate("/app/admin/accounting/forecast"))}
            </StatGrid>

            <StatGrid>
                <StatTile
                    label="Facturas de proveedor vencidas"
                    value={String(supplierCount)}
                    hint={<Typography variant="caption" color="text.secondary">{fmt(data.pendingSupplierBillsAmount)}</Typography>}
                    icon={<Warning fontSize="small"/>}
                    tone={supplierCount > 0 ? "warning" : "neutral"}
                    onClick={() => navigate("/app/admin/accounting/alerts")}
                />
                <StatTile
                    label="Facturas de cliente sin cobrar"
                    value={String(clientCount)}
                    hint={<Typography variant="caption" color="text.secondary">{fmt(data.pendingClientBillsAmount)}</Typography>}
                    icon={<AttachMoney fontSize="small"/>}
                    tone={clientCount > 0 ? "info" : "neutral"}
                    onClick={() => navigate("/app/admin/accounting/alerts")}
                />
            </StatGrid>

            <Stack spacing={3}>
                {(data.draftEntriesCount ?? 0) > 0 && (
                    <Alert severity="warning" sx={{borderRadius: 2.5}}
                           action={<Button size="small" color="inherit" sx={btnSx}
                                           onClick={() => navigate("/app/admin/accounting/journal")}>Ver asientos</Button>}>
                        Hay <strong>{data.draftEntriesCount}</strong> asiento{data.draftEntriesCount === 1 ? "" : "s"} en borrador pendiente{data.draftEntriesCount === 1 ? "" : "s"} de confirmar.
                    </Alert>
                )}

                <Panel title="Acciones rápidas">
                    <Box sx={{display: "flex", gap: 1.5, flexWrap: "wrap"}}>
                        <Button variant="outlined" startIcon={<Assignment/>} sx={btnSx}
                                onClick={() => navigate("/app/admin/accounting/journal/new")}>
                            Nuevo asiento
                        </Button>
                        <Button variant="outlined" startIcon={<AttachMoney/>} sx={btnSx}
                                onClick={() => navigate("/app/admin/accounting/supplier-bill/new")}>
                            Nueva factura recibida
                        </Button>
                        <Button variant="outlined" startIcon={<Warning/>} sx={btnSx}
                                onClick={() => navigate("/app/admin/accounting/alerts")}>
                            Ver alertas de vencimiento
                        </Button>
                        <Button variant="outlined" startIcon={<CompareArrows/>} sx={btnSx}
                                onClick={() => navigate("/app/admin/accounting/reconciliation")}>
                            Conciliación asistida
                        </Button>
                    </Box>
                </Panel>
            </Stack>
        </Page>
    );
};

/**
 * Cierre previsto del año y colchón de tesorería (#14560), con enlace a la previsión. Un array y no un fragmento:
 * StatGrid pone cada hijo en su celda.
 */
const forecastTiles = (forecast: AccountingForecast | null | undefined, onOpen: () => void) => {
    const link = <Button size="small" onClick={onOpen} sx={{...btnSx, ml: -0.75, mt: 0.5}}>Ver previsión</Button>;
    if (forecast === null) {
        return [<StatTile key="close" label="Cierre previsto del año" value="—"
                         hint={<Typography variant="caption" color="text.secondary">No se ha podido calcular</Typography>} footer={link}/>];
    }
    const close = forecast?.result?.closeBeforeTax ?? 0;
    const c = forecast?.cushion;
    const noCushion = c?.months === undefined || c?.months === null;
    return [
            <StatTile key="close" label="Cierre previsto del año" loading={!forecast} value={eur0(close)}
                      icon={close >= 0 ? <TrendingUp fontSize="small"/> : <TrendingDown fontSize="small"/>}
                      tone={!forecast ? "neutral" : close >= 0 ? "success" : "error"}
                      hint={forecast && <Typography variant="caption" color="text.secondary" component="div">
                          Antes de impuestos · de gestión {eur0(forecast.result?.managementBeforeTax)}
                      </Typography>}
                      footer={link}/>,
            <StatTile key="cushion" label="Colchón de tesorería" loading={!forecast}
                      value={noCushion ? "—" : `${months1(c!.months)} meses`}
                      icon={<Savings fontSize="small"/>}
                      tone={!forecast || noCushion ? "neutral" : c!.belowTarget ? "error" : "success"}
                      hint={forecast && <Typography variant="caption" color="text.secondary" component="div">
                          {noCushion ? "Sin costes fijos medidos" : `Objetivo ${months1(c!.targetMonths)} · ${eur0(c!.aboveTarget)} por encima`}
                      </Typography>}
                      footer={link}/>,
    ];
};
